import { type FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { _isNotBlank, _notNil } from '@/littledash';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import VerticalStepForm from '@/components/UI/VerticalStepForm/VerticalStepForm';
import type { StepConfig, LoadOptionsResult } from '@/components/UI/VerticalStepForm/VerticalStepForm.model';
import { agentUrlSlugMap } from '@/components/Agents/AgentUtils';
import { ResponseType } from '@/support/ApiService';
import { web as webRoute } from '@/support/route';
import type { StudyApiId } from '@/model/Study.model';

type StudyHealthSetupValues = {
  study_api_id: StudyApiId;
};

const StudyHealthSetupForm: FC = () => {
  const navigate = useNavigate();
  const { invoke: getStudies } = useApiHook({
    endpoint: 'GET /api/v1/studies',
    options: { invokeOnInit: false, onError: { slug: 'agent-setup-study-load', toast: false, throw: false } },
  });

  const { invoke: createSession } = useApiHook({
    endpoint: 'POST /api/v1/ai-agents/study-health-check',
    options: {
      invokeOnInit: false,
      onError: {
        slug: 'agent-setup-study-health-check-create',
        toast_message: 'Failed to start the study health check. Please try again.',
        throw: false,
      },
    },
  });

  const loadStudies = useCallback(
    async ({ inputValue, page }: { inputValue: string; page: number }): Promise<LoadOptionsResult> => {
      const query: Record<string, string> = {
        perPage: '5',
        page: `${page}`,
      };

      if (_isNotBlank(inputValue)) {
        query.search_by = inputValue.trim();
      }

      let response;
      try {
        response = await getStudies({ query });
      } catch (error) {
        ExceptionHandler.captureException(
          new InVivoError('Error loading studies for agent setup', {
            cause: error,
            slug: 'agent-setup-study-load',
            level: 'warning',
          })
        );
        return { options: [], hasMore: false };
      }

      if (response.type === 'success') {
        const studies = response.body.data ?? [];
        const hasMore = _notNil(response.body.links?.next);

        return {
          options: studies.map((study) => ({
            label: study.code ? `${study.code} · ${study.name}` : study.name,
            value: study.api_id,
          })),
          hasMore,
        };
      }

      return { options: [], hasMore: false };
    },
    [getStudies]
  );

  const handleSubmit = useCallback(
    async (values: StudyHealthSetupValues): Promise<void> => {
      const response = await createSession({ body: { study_api_id: values.study_api_id } });

      if (response.type === ResponseType.Success) {
        const sessionApiId = response.body.data.api_id;
        navigate(webRoute('agents.session', { slug: agentUrlSlugMap.study_health_check, sessionApiId }));
      }
    },
    [createSession, navigate]
  );

  const steps: Array<StepConfig> = [
    {
      id: 'study',
      title: 'Select Study',
      required: true,
      fields: [
        {
          type: 'select',
          name: 'study_api_id',
          label: 'Study',
          placeholder: 'Search for a study...',
          loadOptions: loadStudies,
          rules: { required: 'A study is required' },
        },
      ],
    },
  ];

  return (
    <VerticalStepForm<StudyHealthSetupValues>
      steps={steps}
      onSubmit={handleSubmit}
      description="Configure and run a health check on your study"
      submitDescription="Will scan measurements, observations & dosing and flag anomalies."
    />
  );
};

export default StudyHealthSetupForm;
